import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { api } from '@/api/client'
import type {
  AlertDTO,
  BudgetAlert,
  BudgetAlertSettings,
  BudgetAlertSettingsDTO,
  BudgetAlertHistoryItem,
  BudgetAlertHistoryItemDTO,
  BudgetAlertThreshold,
} from '@/mocks/data/types'

/** Map the backend's snake_case alert DTO to the frontend's camelCase shape. */
function mapAlertFromDTO(dto: AlertDTO): BudgetAlert {
  return {
    id: dto.id,
    campaignId: dto.campaign_id,
    campaignName: dto.campaign_name ?? 'Unknown campaign',
    alertType: dto.alert_type,
    message: dto.message,
    threshold: dto.threshold,
    currentSpend: dto.current_spend,
    budget: dto.budget,
    percentage: dto.percentage,
    triggeredAt: dto.triggered_at,
    // Same derivation list_alert_history uses for `acknowledged`.
    read: dto.status !== 'active',
  }
}

/** Map the backend's snake_case settings DTO to the frontend's camelCase shape. */
function mapSettingsFromDTO(dto: BudgetAlertSettingsDTO): BudgetAlertSettings {
  return {
    thresholds: dto.thresholds,
    enabled: dto.enabled,
    perCampaign: dto.per_campaign,
  }
}

/** Map a partial camelCase settings update to the backend's snake_case DTO. */
function mapSettingsToDTO(settings: Partial<BudgetAlertSettings>): Partial<BudgetAlertSettingsDTO> {
  const dto: Partial<BudgetAlertSettingsDTO> = {}
  if (settings.thresholds !== undefined) dto.thresholds = settings.thresholds
  if (settings.enabled !== undefined) dto.enabled = settings.enabled
  if (settings.perCampaign !== undefined) dto.per_campaign = settings.perCampaign
  return dto
}

/** Map the backend's snake_case history-item DTO to the frontend's camelCase shape. */
function mapHistoryItemFromDTO(dto: BudgetAlertHistoryItemDTO): BudgetAlertHistoryItem {
  return {
    id: dto.id,
    campaignId: dto.campaign_id,
    campaignName: dto.campaign_name ?? 'Unknown campaign',
    threshold: dto.threshold,
    currentSpend: dto.current_spend,
    budget: dto.budget,
    percentage: dto.percentage,
    triggeredAt: dto.triggered_at,
    acknowledged: dto.acknowledged,
    acknowledgedAt: dto.acknowledged_at ?? undefined,
  }
}

interface BudgetAlertsState {
  // Current alerts (unread/active)
  alerts: BudgetAlert[]
  // Alert settings
  settings: BudgetAlertSettings
  // Alert history
  history: BudgetAlertHistoryItem[]
  // Loading states
  isLoading: boolean
  isSettingsLoading: boolean
  isHistoryLoading: boolean
  // Error state
  error: string | null
}

interface BudgetAlertsActions {
  // Fetch current alerts
  fetchAlerts: () => Promise<void>
  // Fetch settings
  fetchSettings: () => Promise<void>
  // Update settings
  updateSettings: (settings: Partial<BudgetAlertSettings>) => Promise<void>
  // Toggle threshold
  toggleThreshold: (threshold: BudgetAlertThreshold) => Promise<void>
  // Toggle alerts for a specific campaign
  toggleCampaignAlerts: (campaignId: string, enabled: boolean) => Promise<void>
  // Mark alert as read
  markAlertAsRead: (alertId: string) => void
  // Mark all alerts as read
  markAllAlertsAsRead: () => void
  // Fetch history
  fetchHistory: () => Promise<void>
  // Acknowledge history item
  acknowledgeHistoryItem: (itemId: string) => void
  // Get unread count
  getUnreadCount: () => number
}

type BudgetAlertsStore = BudgetAlertsState & BudgetAlertsActions

const initialState: BudgetAlertsState = {
  alerts: [],
  settings: {
    thresholds: [50, 80, 100],
    enabled: true,
    perCampaign: {},
  },
  history: [],
  isLoading: false,
  isSettingsLoading: false,
  isHistoryLoading: false,
  error: null,
}

export const useBudgetAlertsStore = create<BudgetAlertsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchAlerts: async () => {
        set({ isLoading: true, error: null })
        try {
          // Paginated {items, total, limit, offset} envelope (AlertListResponse).
          const response = await api.getList<AlertDTO>('/ads/alerts')
          set({ alerts: response.data.map(mapAlertFromDTO), isLoading: false })
        } catch {
          set({ error: 'Failed to fetch alerts', isLoading: false })
        }
      },

      fetchSettings: async () => {
        set({ isSettingsLoading: true, error: null })
        try {
          const response = await api.get<BudgetAlertSettingsDTO>('/ads/settings/alerts')
          if (response) {
            set({ settings: mapSettingsFromDTO(response), isSettingsLoading: false })
          }
        } catch {
          set({ error: 'Failed to fetch settings', isSettingsLoading: false })
        }
      },

      updateSettings: async (newSettings: Partial<BudgetAlertSettings>) => {
        const currentSettings = get().settings
        const updatedSettings = { ...currentSettings, ...newSettings }
        set({ isSettingsLoading: true, error: null })
        try {
          const response = await api.put<BudgetAlertSettingsDTO>(
            '/ads/settings/alerts',
            mapSettingsToDTO(updatedSettings)
          )
          if (response) {
            set({ settings: mapSettingsFromDTO(response), isSettingsLoading: false })
          }
        } catch {
          set({ error: 'Failed to update settings', isSettingsLoading: false })
        }
      },

      toggleThreshold: async (threshold: BudgetAlertThreshold) => {
        const currentSettings = get().settings
        const currentThresholds = currentSettings.thresholds
        const newThresholds = currentThresholds.includes(threshold)
          ? currentThresholds.filter((t) => t !== threshold)
          : ([...currentThresholds, threshold].sort((a, b) => a - b) as BudgetAlertThreshold[])

        await get().updateSettings({ thresholds: newThresholds })
      },

      toggleCampaignAlerts: async (campaignId: string, enabled: boolean) => {
        const currentSettings = get().settings
        const newPerCampaign = {
          ...currentSettings.perCampaign,
          [campaignId]: enabled,
        }
        await get().updateSettings({ perCampaign: newPerCampaign })
      },

      markAlertAsRead: (alertId: string) => {
        set((state) => ({
          alerts: state.alerts.map((alert) =>
            alert.id === alertId ? { ...alert, read: true } : alert
          ),
        }))
      },

      markAllAlertsAsRead: () => {
        set((state) => ({
          alerts: state.alerts.map((alert) => ({ ...alert, read: true })),
        }))
      },

      fetchHistory: async () => {
        set({ isHistoryLoading: true, error: null })
        try {
          // Paginated: the backend returns {items, total, limit, offset}
          // (BudgetAlertHistoryListResponse), which api/client.ts deliberately
          // leaves intact for getList()'s normalizePaginatedResponse().
          const response = await api.getList<BudgetAlertHistoryItemDTO>('/ads/alerts/history')
          set({ history: response.data.map(mapHistoryItemFromDTO), isHistoryLoading: false })
        } catch {
          set({ error: 'Failed to fetch history', isHistoryLoading: false })
        }
      },

      acknowledgeHistoryItem: (itemId: string) => {
        set((state) => ({
          history: state.history.map((item) =>
            item.id === itemId
              ? { ...item, acknowledged: true, acknowledgedAt: new Date().toISOString() }
              : item
          ),
        }))
      },

      getUnreadCount: () => {
        return get().alerts.filter((alert) => !alert.read).length
      },
    }),
    { name: 'BudgetAlertsStore', enabled: import.meta.env.DEV }
  )
)

// Selectors
export const selectAlerts = (state: BudgetAlertsStore) => state.alerts
export const selectUnreadAlerts = (state: BudgetAlertsStore) =>
  state.alerts.filter((alert) => !alert.read)
export const selectSettings = (state: BudgetAlertsStore) => state.settings
export const selectHistory = (state: BudgetAlertsStore) => state.history
export const selectIsLoading = (state: BudgetAlertsStore) => state.isLoading
